import {addBreadcrumb} from '@sentry/react'
import {API_BASE_URL} from '@/lib/config'
import {getImpersonationTarget} from '@/stores/impersonation-store'

type ResponseType = 'json' | 'blob' | 'text'

type ApiClientOptions = {
	method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
	body?: unknown
	responseType?: ResponseType
	signal?: AbortSignal
}

/**
 * `requestId` is the API-side correlation key (echoed via the `X-Request-Id`
 * response header). When triaging "got a 400, why?", search the API logs for
 * this id and you'll get one matching log line — no timestamp guessing.
 */
export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
		public requestId: string | null = null,
	) {
		super(message)
		this.name = 'ApiError'
	}
}

async function parseErrorMessage(response: Response): Promise<string> {
	try {
		const errorData = await response.json()
		return errorData.error || errorData.message || 'Request failed'
	} catch {
		// JSON parsing failed — fall back to text or generic message
	}

	try {
		return await response.text()
	} catch {
		return `Request failed with status ${response.status}`
	}
}

/**
 * Reusable API client for making authenticated requests
 *
 * Automatically injects Clerk authentication token if available.
 *
 * @param endpoint - API endpoint (e.g., '/api/users')
 * @param options - Request options
 * @returns Parsed response (JSON, Blob, or Text based on responseType)
 * @throws ApiError with status code and error message
 *
 * @example
 * // JSON response (default)
 * const users = await apiClient<User[]>('/api/users')
 *
 * @example
 * // File download (blob)
 * const blob = await apiClient<Blob>('/api/permits/export', {
 *   method: 'POST',
 *   body: filters,
 *   responseType: 'blob'
 * })
 */
export async function apiClient<T>(
	endpoint: string,
	options?: ApiClientOptions,
): Promise<T> {
	const {method = 'GET', body, responseType = 'json', signal} = options ?? {}

	const authToken = await window.Clerk?.session?.getToken()
	const target = getImpersonationTarget()

	const headers: Record<string, string> = {}
	if (authToken) headers.Authorization = `Bearer ${authToken}`
	if (target) headers['X-Impersonate-User'] = target.id
	if (body) headers['Content-Type'] = 'application/json'

	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method,
		headers,
		body: body ? JSON.stringify(body) : undefined,
		signal,
	})

	if (!response.ok) {
		const errorMessage = await parseErrorMessage(response)
		const requestId = response.headers.get('X-Request-Id')
		addBreadcrumb({
			category: 'api',
			level: response.status >= 500 ? 'error' : 'warning',
			message: `${method} ${endpoint} → ${response.status}`,
			data: {requestId, status: response.status, endpoint, method},
		})
		throw new ApiError(response.status, errorMessage, requestId)
	}

	switch (responseType) {
		case 'blob':
			return response.blob() as T
		case 'text':
			return response.text() as T
		default:
			return response.json()
	}
}

// TypeScript declaration for Clerk global object
declare global {
	interface Window {
		Clerk?: {
			session?: {
				getToken: () => Promise<string | null>
			}
		}
	}
}
